import React, { memo } from 'react';
import { forwardRef, useEffect } from 'react';
import { classNames } from '~/utils/classNames';
import { forwardRefMemo } from '~/utils/react';

const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);

const Container = forwardRef<
  HTMLButtonElement,
  {
    action?: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    labelPosition?: 'start' | 'end';
  }
>(({ action, children, disabled, labelPosition }, ref) => {
  if (action) {
    return (
      <button
        ref={ref}
        disabled={disabled}
        tabIndex={-1}
        className={classNames(
          'flex items-center gap-2 p-1 rounded transition-theme bg-transparent hover:bg-bolt-elements-button-secondary-background outline-none',
          {
            'opacity-25': disabled,
            'pl-2': labelPosition === 'start',
            'pr-2': labelPosition === 'end',
          },
        )}
        onClick={action}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={classNames('flex items-center gap-2 transition-theme', { 'opacity-25': disabled })}>{children}</div>
  );
});

const PlainKeyboardShortcut = memo(({ keys }: { keys: string[] }) => {
  return (
    <div className="inline-flex items-center gap-0.5">
      {keys.map((key, index) => {
        const displayKey = getDisplayKey(key);

        return (
          <React.Fragment key={key}>
            {index > 0 && !isMac && <span>+</span>}
            {displayKey.startsWith('i-') ? (
              <span className="size-3 shrink-0 flex items-center justify-center">
                <span
                  className={classNames(displayKey, 'shrink-0', {
                    'size-4': key === 'Shift',
                  })}
                />
              </span>
            ) : (
              <span>{displayKey}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

PlainKeyboardShortcut.displayName = 'PlainKeyboardShortcut';

function getDisplayKey(key: string) {
  if (key === 'Meta') {
    return isMac ? 'i-lucide:command' : 'Ctrl';
  }

  if (key === 'Shift') {
    return 'i-lucide:arrow-big-up';
  }

  if (key === 'Control') {
    return 'i-lucide:chevron-up';
  }

  if (key === 'Alt') {
    return 'i-lucide:option';
  }

  if (key === 'Tab') {
    return 'i-lucide:arrow-right-to-line';
  }

  if (key === 'Enter') {
    return 'i-lucide:corner-down-left';
  }

  if (key === 'Escape') {
    return 'Esc';
  }

  return key;
}

const KeyboardKey = memo(({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-bolt-ds-neutral text-bolt-ds-on-neutral flex px-1.5 text-xs min-w-5.5 h-5.5 gap-2 items-center justify-center rounded">
      {children}
    </div>
  );
});

KeyboardKey.displayName = 'KeyboardKey';

const KeyboardShortcut = forwardRefMemo<
  HTMLButtonElement,
  {
    label?: string;
    icon?: string;
    labelPosition?: 'start' | 'end';
    keys: string[];
    disabled?: boolean;
    action?: () => void;
    hidden?: boolean;
  }
>(({ label, labelPosition = 'start', icon, keys, disabled, action, hidden }, ref) => {
  const keysForPlatform = keys.map((key) => {
    if (key === 'Meta') {
      return isMac ? 'Meta' : 'ControlLeft';
    }

    if (key === 'Esc') {
      return 'Escape';
    }

    if (key === 'Del') {
      return isMac ? 'Backspace' : 'Delete';
    }

    return key;
  });

  useEffect(() => {
    if (!action || disabled) {
      return undefined;
    }

    function handleKeydown(e: KeyboardEvent) {
      const key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
      const metaKey = e.metaKey ? 'Meta' : '';
      const altKey = e.altKey ? 'Alt' : '';
      const shiftKey = e.shiftKey ? 'Shift' : '';
      const controlKey = e.ctrlKey ? 'Control' : '';

      const pressedKeys = [metaKey, altKey, shiftKey, controlKey, key].filter(Boolean);

      if (pressedKeys.join('') === keysForPlatform.join('')) {
        e.preventDefault();
        action?.();
      }
    }

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [action, keys]);

  if (hidden) {
    return null;
  }

  const keysMap = keys.map((key) => ({
    key,
    displayKey: getDisplayKey(key),
  }));

  const labelComponent = label ? (
    <span className="text-xs text-bolt-ds-textTertiary font-medium">{label}</span>
  ) : icon ? (
    <span className={classNames(icon, 'size-3 flex items-center justify-center')}></span>
  ) : null;

  return (
    <Container action={action} disabled={disabled} labelPosition={labelPosition} ref={ref}>
      {labelPosition === 'start' && labelComponent}

      <div className="flex items-center gap-1">
        {keysMap.map(({ key, displayKey }) => (
          <KeyboardKey key={key}>
            {displayKey.startsWith('i-') ? (
              <span className="size-3 flex items-center justify-center">
                <span
                  className={classNames(displayKey, 'shrink-0', {
                    'size-4': key === 'Shift',
                  })}
                />
              </span>
            ) : (
              displayKey
            )}
          </KeyboardKey>
        ))}
      </div>

      {labelPosition === 'end' && labelComponent}
    </Container>
  );
});

KeyboardShortcut.displayName = 'KeyboardShortcut';

export { KeyboardShortcut, KeyboardKey, PlainKeyboardShortcut };
